import { useState } from 'react';
import { createChild, updateChild } from '../services/children';

/**
 * Create or edit a child — a login-less wish-list owner managed by one or more adults.
 *
 * Deliberately asks for very little: a first name and an optional birthday. That is the
 * least that makes a list usable, and it is a child's data. Anything more would have to
 * justify itself in the privacy policy — see LAUNCH_READINESS.md §7.1 and the Children
 * section of PrivacyPolicy.jsx.
 */
export default function ChildFormModal({ groupId, child, onClose, onSaved, notify }) {
    const editing = Boolean(child);
    const [name, setName] = useState(child?.name || '');
    const [birthday, setBirthday] = useState(child?.birthday || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = { name: name.trim(), birthday: birthday || null };
            const saved = editing
                ? await updateChild(child.id, payload)
                : await createChild(groupId, payload);
            notify?.('success', editing ? 'Saved.' : `${saved.name} was added.`);
            onSaved(saved);
        } catch (err) {
            setError(err.response?.data?.error || 'Could not save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500';

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold mb-1">
                    {editing ? `Edit ${child.name}` : 'Add a child'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                    A child doesn&rsquo;t sign in. You keep their list on your account, and you
                    choose who else can add to it.
                </p>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            First name <span className="text-red-500">*</span>
                        </label>
                        <input
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={inputCls}
                            placeholder="Maya"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Birthday (optional)
                        </label>
                        <input
                            type="date"
                            value={birthday || ''}
                            onChange={(e) => setBirthday(e.target.value)}
                            className={inputCls}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Used to show a countdown in the group. Leave it blank if you&rsquo;d
                            rather not record it.
                        </p>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || !name.trim()}
                            className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : editing ? 'Save' : 'Add child'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
